import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';

export type ApplicationFontId =
  | 'poppins'
  | 'inter'
  | 'system'
  | 'atkinson'
  | 'roboto'
  | 'open-sans'
  | 'lato'
  | 'montserrat'
  | 'source-sans-3'
  | 'dm-sans'
  | 'plus-jakarta-sans'
  | 'work-sans';

export interface ApplicationFontOption {
  value: ApplicationFontId;
  label: string;
  description: string;
  icon: string;
  cssValue: string;
}

const FONT_STORAGE_KEY = 'preferences.applicationFont';
const SYSTEM_FONT_STACK = "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";

const DEFAULT_APPLICATION_FONT_OPTION: ApplicationFontOption = {
  value: 'poppins',
  label: 'Poppins',
  description: 'Platfio default',
  icon: 'fa-solid fa-font',
  cssValue: `'Poppins', ${SYSTEM_FONT_STACK}`
};

export const APPLICATION_FONT_OPTIONS: readonly ApplicationFontOption[] = [
  DEFAULT_APPLICATION_FONT_OPTION,
  {
    value: 'inter',
    label: 'Inter',
    description: 'Dense and crisp',
    icon: 'fa-solid fa-align-left',
    cssValue: `'Inter', ${SYSTEM_FONT_STACK}`
  },
  {
    value: 'roboto',
    label: 'Roboto',
    description: 'Familiar product UI',
    icon: 'fa-solid fa-align-left',
    cssValue: `'Roboto', ${SYSTEM_FONT_STACK}`
  },
  {
    value: 'open-sans',
    label: 'Open Sans',
    description: 'Friendly and neutral',
    icon: 'fa-solid fa-align-left',
    cssValue: `'Open Sans', ${SYSTEM_FONT_STACK}`
  },
  {
    value: 'lato',
    label: 'Lato',
    description: 'Warm and professional',
    icon: 'fa-solid fa-align-left',
    cssValue: `'Lato', ${SYSTEM_FONT_STACK}`
  },
  {
    value: 'montserrat',
    label: 'Montserrat',
    description: 'Polished and geometric',
    icon: 'fa-solid fa-align-left',
    cssValue: `'Montserrat', ${SYSTEM_FONT_STACK}`
  },
  {
    value: 'source-sans-3',
    label: 'Source Sans 3',
    description: 'Clean interface text',
    icon: 'fa-solid fa-align-left',
    cssValue: `'Source Sans 3', ${SYSTEM_FONT_STACK}`
  },
  {
    value: 'dm-sans',
    label: 'DM Sans',
    description: 'Modern SaaS feel',
    icon: 'fa-solid fa-align-left',
    cssValue: `'DM Sans', ${SYSTEM_FONT_STACK}`
  },
  {
    value: 'plus-jakarta-sans',
    label: 'Plus Jakarta Sans',
    description: 'Sharp dashboard style',
    icon: 'fa-solid fa-align-left',
    cssValue: `'Plus Jakarta Sans', ${SYSTEM_FONT_STACK}`
  },
  {
    value: 'work-sans',
    label: 'Work Sans',
    description: 'Readable workspaces',
    icon: 'fa-solid fa-align-left',
    cssValue: `'Work Sans', ${SYSTEM_FONT_STACK}`
  },
  {
    value: 'system',
    label: 'System',
    description: 'Matches this device',
    icon: 'fa-solid fa-desktop',
    cssValue: SYSTEM_FONT_STACK
  },
  {
    value: 'atkinson',
    label: 'Atkinson',
    description: 'Readable at small sizes',
    icon: 'fa-solid fa-eye',
    cssValue: `'Atkinson Hyperlegible', ${SYSTEM_FONT_STACK}`
  }
];

@Injectable({
  providedIn: 'root'
})
export class ApplicationFontService {
  private readonly defaultFont: ApplicationFontId = DEFAULT_APPLICATION_FONT_OPTION.value;
  private readonly isBrowser: boolean;
  private readonly fontSubject = new BehaviorSubject<ApplicationFontId>(this.defaultFont);

  readonly font$ = this.fontSubject.asObservable();
  readonly options = APPLICATION_FONT_OPTIONS;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.init();
  }

  get font(): ApplicationFontId {
    return this.fontSubject.value;
  }

  get currentFontLabel(): string {
    return this.getFontOption(this.font).label;
  }

  isSupportedFont(value: unknown): value is ApplicationFontId {
    return typeof value === 'string' && APPLICATION_FONT_OPTIONS.some(option => option.value === value);
  }

  setFont(font: ApplicationFontId, persist = true): void {
    const fontOption = this.getFontOption(font);
    this.fontSubject.next(fontOption.value);

    if (!this.isBrowser) {
      return;
    }

    document.documentElement.style.setProperty('--platfio-app-font-family', fontOption.cssValue);
    document.documentElement.setAttribute('data-application-font', fontOption.value);

    if (persist) {
      localStorage.setItem(FONT_STORAGE_KEY, fontOption.value);
    }
  }

  private init(): void {
    const storedFont = this.getStoredFont();
    this.setFont(storedFont || this.defaultFont, false);
  }

  private getStoredFont(): ApplicationFontId | null {
    if (!this.isBrowser) {
      return null;
    }

    const storedFont = localStorage.getItem(FONT_STORAGE_KEY);
    return this.isSupportedFont(storedFont) ? storedFont : null;
  }

  private getFontOption(font: ApplicationFontId): ApplicationFontOption {
    return APPLICATION_FONT_OPTIONS.find(option => option.value === font)
      || DEFAULT_APPLICATION_FONT_OPTION;
  }
}
